import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Spinner, Card, Badge, Accordion } from "react-bootstrap";
import axios from "axios";
import AxioAvatar from "../avatars-256X256/Axio.png";

const AxioChat = ({ usuario }) => {
  const [messages, setMessages] = useState([
    { 
      sender: 'axio', 
      text: '¡Hola! Soy Axio, tu experto en Inteligencia de Trabajo. Describe tu idea o proyecto y generaré una estructura completa (Épica, Historias/Columnas y Tareas) compatible con Scrum o Kanban para ti. He optimizado la generación para agrupar tareas en unidades de trabajo significativas.',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [backlog, setBacklog] = useState(null);
  const [methodology, setMethodology] = useState('scrum');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendQuestion = async () => {
    if (!question.trim()) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMessage = { sender: 'user', text: question, timestamp };
    
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setLoading(true);
    setBacklog(null);

    try {
      // Llamar directamente al microservicio de Axio
      const response = await axios.post("https://axio-service-220765375792.us-central1.run.app/axio/generate", {
        input: userMessage.text,
        ruc: usuario?.ruc || usuario?.rucEmpresa,
        dni: usuario?.dni || "WEB_USER",
        methodology: methodology
      });

      console.log("Respuesta de AXIO:", response.data);

      const data = response.data;
      if (data.epic) {
        setBacklog(data);
        const axioMessage = { 
          sender: 'axio', 
          text: `He generado una nueva estructura de trabajo basada en tu idea. Aquí tienes la Épica: "${data.epic.title}". He guardado todo en el Backlog del sistema.`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isSuccess: true
        };
        setMessages(prev => [...prev, axioMessage]);
      } else {
        throw new Error("La respuesta no contiene una estructura válida.");
      }
    } catch (error) {
      console.error("Error en Axio Chat:", error);
      const errorMessage = { 
        sender: 'axio', 
        text: `Lo siento, hubo un error al procesar tu idea: ${error.message}. Por favor intenta ser más descriptivo.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const styles = {
    container: {
      maxWidth: '1000px',
      margin: '20px auto',
      height: '800px',
      display: 'grid',
      gridTemplateColumns: backlog ? '1fr 1fr' : '1fr',
      gap: '20px',
      transition: 'all 0.5s ease'
    },
    chatSide: {
      background: '#f8f9fa',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      border: '1px solid #e0e0e0'
    },
    backlogSide: {
      background: 'white',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      padding: '25px',
      overflowY: 'auto',
      border: '1px solid #00c6ff',
      animation: 'slideIn 0.4s ease-out'
    },
    header: {
      background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
      color: 'white',
      padding: '15px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    avatar: {
      width: '45px',
      height: '45px',
      borderRadius: '12px',
      background: 'white',
      padding: '2px'
    },
    avatarImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '10px'
    },
    messageList: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    bubble: {
      padding: '12px 16px',
      borderRadius: '15px',
      fontSize: '14px',
      maxWidth: '85%'
    },
    bubbleUser: {
      background: '#0072ff',
      color: 'white',
      alignSelf: 'flex-end',
      borderBottomRightRadius: '2px'
    },
    bubbleAxio: {
      background: 'white',
      color: '#333',
      alignSelf: 'flex-start',
      borderBottomLeftRadius: '2px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
      border: '1px solid #eee'
    },
    inputArea: {
      padding: '15px 20px',
      borderTop: '1px solid #eee',
      background: 'white'
    },
    inputGroup: {
      display: 'flex',
      gap: '10px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Lado del Chat */}
      <div style={styles.chatSide}>
        <div style={styles.header}>
          <div style={styles.avatar}>
            <img src={AxioAvatar} alt="Axio" style={styles.avatarImage} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold">Axio Work Intelligence</h5>
            <small style={{opacity: 0.8}}>Agente de Estructuración {methodology === 'scrum' ? 'Scrum' : 'Kanban'}</small>
          </div>
        </div>

        <div style={styles.messageList}>
          {messages.map((msg, i) => (
            <div key={i} style={{
              ...styles.bubble,
              ...(msg.sender === 'user' ? styles.bubbleUser : styles.bubbleAxio),
              ...(msg.isError ? { background: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030' } : {})
            }}>
              {msg.text}
              <div style={{fontSize: '10px', marginTop: '4px', opacity: 0.6, textAlign: 'right'}}>
                {msg.timestamp}
              </div>
            </div>
          ))}
          {loading && (
            <div style={styles.bubbleAxio}>
              <Spinner animation="grow" size="sm" variant="primary" />
              <Spinner animation="grow" size="sm" variant="info" className="mx-1" />
              <Spinner animation="grow" size="sm" variant="primary" />
              <small className="ms-2">Analizando idea y generando backlog...</small>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div style={styles.inputArea}>
          <div className="d-flex gap-2 mb-3 justify-content-center">
            <Button 
              variant={methodology === 'scrum' ? 'primary' : 'outline-primary'} 
              size="sm" 
              onClick={() => setMethodology('scrum')}
              style={{borderRadius: '10px'}}
            >
              <i className="fa fa-sync me-1"></i> Modo Scrum
            </Button>
            <Button 
              variant={methodology === 'kanban' ? 'info' : 'outline-info'} 
              size="sm" 
              onClick={() => setMethodology('kanban')}
              style={{borderRadius: '10px'}}
            >
              <i className="fa fa-columns me-1"></i> Modo Kanban
            </Button>
          </div>
          <Form onSubmit={(e) => { e.preventDefault(); sendQuestion(); }}>
            <div style={styles.inputGroup}>
              <textarea
                ref={inputRef}
                className="form-control"
                style={{borderRadius: '15px', resize: 'none'}}
                placeholder="Ej: Quiero crear un módulo de facturación electrónica con reportes..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                rows={2}
              />
              <Button 
                type="submit" 
                variant="primary" 
                style={{borderRadius: '15px', width: '60px'}}
                disabled={loading || !question.trim()}
              >
                <i className="fa fa-paper-plane"></i>
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* Lado del Backlog (Aparece cuando hay resultado) */}
      {backlog && (
        <div style={styles.backlogSide}>
          <h4 className="fw-bold mb-4 d-flex align-items-center">
            <Badge bg="primary" className="me-2">{methodology === 'scrum' ? 'ÉPICA' : 'TABLERO'}</Badge>
            {backlog.epic.title}
          </h4>
          <p className="text-muted mb-4">{backlog.epic.description}</p>
          
          <h5 className="mb-3">{methodology === 'scrum' ? 'Historias de Usuario' : 'Columnas de Trabajo'} ({backlog.stories.length})</h5>
          <Accordion defaultActiveKey="0">
            {backlog.stories.map((story, sIdx) => (
              <Accordion.Item eventKey={sIdx.toString()} key={sIdx} className="mb-2 shadow-sm border-0">
                <Accordion.Header>
                  <div className="d-flex w-100 justify-content-between align-items-center pe-3">
                    <span className="fw-semibold">{methodology === 'scrum' ? 'HU: ' : ''}{story.title}</span>
                    <Badge bg="info" pill>BACKLOG</Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <p className="small text-muted mb-3">{story.description}</p>
                  <div className="ps-3 border-start border-3 border-info">
                    <h6 className="small fw-bold mb-2 text-primary">{methodology === 'scrum' ? 'Tareas Técnicas:' : 'Tarjetas:'}</h6>
                    <ul className="list-unstyled mb-0">
                      {story.tasks.map((task, tIdx) => (
                        <li key={tIdx} className="mb-2 d-flex align-items-start p-2 rounded" style={{background: '#f8f9fa'}}>
                          <div className="me-2 mt-1">
                             <div style={{width: '18px', height: '18px', border: '2px solid #00c6ff', borderRadius: '4px', cursor: 'pointer'}}></div>
                          </div>
                          <div>
                            <span className="small d-block fw-bold">{task.title}</span>
                            <span className="x-small text-muted" style={{fontSize: '11px'}}>{task.description}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      )}

      <style jsx>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .x-small { font-size: 11px; }
      `}</style>
    </div>
  );
};

export default AxioChat;
